import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Book, Layout, Map, MessageSquare, LogOut, FileText, CreditCard, ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from './firebase/config';
import { signOut } from 'firebase/auth';
import TabButton from './components/TabButton';
import { Button } from './components/ui/button';
import { API_URL } from './config';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CustomMindmapViewer from './components/CustomMindmapViewer';
import mermaid from 'mermaid';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import PaperSummaryView from './components/PaperSummaryView';
import McqView from './components/McqView';
import ChatView from './components/ChatView';
import ReadPaperView from './components/ReadPaperView';
import LogoutButton from './components/LogoutButton';

const paperStyles = `
.paper-content {
    font-family: 'Inter', sans-serif;
}

.paper-content h1 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 2rem;
    color: white;
    line-height: 1.2;
}

.paper-content h2 {
    font-size: 1.8rem;
    font-weight: 600;
    margin-top: 2rem;
    margin-bottom: 1rem;
    color: #e5e7eb;
}

.paper-content h3 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-top: 1.5rem;
    margin-bottom: 0.75rem;
    color: #e5e7eb;
}

.paper-content p {
    margin-bottom: 1.25rem;
    line-height: 1.7;
    color: #d1d5db;
}

.paper-content strong {
    color: white;
    font-weight: 600;
}

.paper-content ul, .paper-content ol {
    margin-left: 1.5rem;
    margin-bottom: 1.25rem;
}

.paper-content li {
    margin-bottom: 0.5rem;
    color: #d1d5db;
}

.paper-content figure {
    margin: 2rem 0;
}

.paper-content img {
    max-width: 100%;
    height: auto;
    border-radius: 0.5rem;
}

.paper-content figcaption {
    text-align: center;
    font-size: 0.875rem;
    color: #9ca3af;
    margin-top: 0.5rem;
}
`;

const styleSheet = document.createElement("style");
styleSheet.textContent = paperStyles;
document.head.appendChild(styleSheet);

mermaid.initialize({
    startOnLoad: true,
    theme: 'dark',
    mindmap: {
        padding: 20,
        useMaxWidth: true,
        wrap: true,
        nodeSpacing: 100,
        rankSpacing: 80,
    },
    flowchart: {
        htmlLabels: true,
        wrap: true,
        useMaxWidth: true,
    }
});

const MAX_STATUS_CHECK_ATTEMPTS = 20;
const STATUS_CHECK_INTERVAL = 20000; // 20 seconds in milliseconds

const QUERY_CONFIG = {
    staleTime: Infinity,
    cacheTime: 24 * 60 * 60 * 1000,
    retry: false,
};

const STATUS_CHECK_CONFIG = {
    ...QUERY_CONFIG,
    retry: true,
    retryDelay: STATUS_CHECK_INTERVAL,
};

const WorkspacePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('summary');
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Initialize currentPaper from location state or localStorage
    const [currentPaper, setCurrentPaper] = useState(() => {
        // First try to get from location state
        if (location.state?.paper) {
            // Save to localStorage when we get from location state
            localStorage.setItem('currentPaper', JSON.stringify(location.state.paper));
            return location.state.paper;
        }

        // If not in location state, try localStorage
        const savedPaper = localStorage.getItem('currentPaper');
        if (savedPaper) {
            return JSON.parse(savedPaper);
        }

        return null;
    });

    // Update localStorage when currentPaper changes
    useEffect(() => {
        if (currentPaper) {
            localStorage.setItem('currentPaper', JSON.stringify(currentPaper));
        }
    }, [currentPaper]);

    const [authToken, setAuthToken] = useState(null);
    const [topics, setTopics] = useState(null);
    const [isGeneratingTopics, setIsGeneratingTopics] = useState(false);
    const [generatingMindmapForTopic, setGeneratingMindmapForTopic] = useState(null);
    const [generatedMindmaps, setGeneratedMindmaps] = useState([]);
    const [paperHtml, setPaperHtml] = useState(null);
    const [isLoadingHtml, setIsLoadingHtml] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [notes, setNotes] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [topicsCache, setTopicsCache] = useState({});
    const [question, setQuestion] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [isAskingQuestion, setIsAskingQuestion] = useState(false);
    const [hasLibrary, setHasLibrary] = useState(true);
    const [htmlCache, setHtmlCache] = useState({});
    const [notesCache, setNotesCache] = useState({});
    const queryClient = useQueryClient();
    const [mountedPapers, setMountedPapers] = useState({});
    const MAX_CACHED_PDFS = 5; // Adjust based on your needs
    const [summaryData, setSummaryData] = useState(null);
    const [selectedMindmap, setSelectedMindmap] = useState(null);
    const [mindmapCache, setMindmapCache] = useState({});
    const [initialLoadStartTime, setInitialLoadStartTime] = useState(null);
    const [statusCheckAttempts, setStatusCheckAttempts] = useState(0);
    const [statusCheckFailed, setStatusCheckFailed] = useState(false);
    const [summaryCache, setSummaryCache] = useState({});
    const [statusCache, setStatusCache] = useState({});
    const [mindmapStatusCheckAttempts, setMindmapStatusCheckAttempts] = useState(0);
    const [mindmapStatusCheckFailed, setMindmapStatusCheckFailed] = useState(false);
    const [chatStatusCheckAttempts, setChatStatusCheckAttempts] = useState(0);
    const [chatStatusCheckFailed, setChatStatusCheckFailed] = useState(false);
    const [chatCache, setChatCache] = useState({});
    const [chats, setChats] = useState([]);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [isCreatingChat, setIsCreatingChat] = useState(false);
    const [newChatTitle, setNewChatTitle] = useState('');
    const [optimisticChats, setOptimisticChats] = useState([]);
    const [mcqAnswers, setMcqAnswers] = useState(() => {
        const saved = localStorage.getItem('mcqAnswers');
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                const token = await user.getIdToken();
                setAuthToken(token);
            } else {
                setAuthToken(null);
                navigate('/');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    const { data: libraryPapers = [], isLoading } = useQuery({
        queryKey: ['libraryPapers'],
        queryFn: async () => {
            if (!authToken) return [];

            const response = await fetch(`${API_URL}/papers/library`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (response.status === 200 && data.success === false && data.message === "No library found") {
                setHasLibrary(false);
                return [];
            }

            if (!response.ok) {
                if (response.status === 401) {
                    navigate('/');
                    return [];
                }
                throw new Error('Failed to fetch library papers');
            }

            setHasLibrary(true);
            return Object.entries(data.papers || {}).map(([arxivId, paper]) => ({
                id: arxivId,
                ...paper
            }));
        },
        ...QUERY_CONFIG,
        enabled: !!authToken && hasLibrary,
    });

    const { data: savedNotes = [], refetch: refetchNotes } = useQuery({
        queryKey: ['notes', currentPaper?.id, authToken],
        queryFn: async () => {
            if (!authToken || !currentPaper) return [];

            // if currentPaper.id starts with "http" then get the arxivId from the url
            if (currentPaper.id.startsWith("http")) {
                const match = currentPaper.id.match(/arxiv\.org\/abs\/([0-9]+\.[0-9]+)/);
                if (match) {
                    currentPaper.id = match[1];
                }
            }

            // Check cache first
            if (notesCache[currentPaper.id]) {
                return notesCache[currentPaper.id];
            }

            const response = await fetch(`${API_URL}/notes/${currentPaper.id}`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            const data = await response.json();

            console.log("notes for paper", currentPaper.id, data);

            if (response.status === 404 && data.message === "No library found") {
                setHasLibrary(false);
                return [];
            }

            if (!response.ok) {
                if (response.status === 401) {
                    navigate('/');
                    return [];
                }
                throw new Error('Failed to fetch notes');
            }

            setHasLibrary(true);
            const notes = data.notes || [];

            // Cache the notes
            setNotesCache(prev => ({
                ...prev,
                [currentPaper.id]: notes
            }));

            return notes;
        },
        enabled: !!authToken && !!currentPaper && hasLibrary,
        retry: false,
    });

    const { data: paperStatus } = useQuery({
        queryKey: ['paperStatus', currentPaper?.id],
        queryFn: async () => {
            if (!authToken || !currentPaper) return null;

            // Check cache first
            if (statusCache[currentPaper.id]) {
                return statusCache[currentPaper.id];
            }

            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/summary/status`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch paper status');
            }

            const data = await response.json();

            // Cache completed or error statuses
            if (data.status === 'completed' || data.status === 'error') {
                setStatusCache(prev => ({
                    ...prev,
                    [currentPaper.id]: data
                }));
            }

            // If still processing, throw error to trigger retry
            if (data.status === 'processing' && statusCheckAttempts < MAX_STATUS_CHECK_ATTEMPTS) {
                setStatusCheckAttempts(prev => prev + 1);
                throw new Error('Still processing');
            }

            return data;
        },
        ...STATUS_CHECK_CONFIG,
        enabled: !!authToken && !!currentPaper && !statusCache[currentPaper?.id],
    });

    // Add this useEffect to reset status check attempts when paper changes
    useEffect(() => {
        setStatusCheckAttempts(0);
        setStatusCheckFailed(false);
    }, [currentPaper]);

    // Modify the existing summary query to only be enabled when needed
    const { data: paperSummary, isLoading: isLoadingSummary } = useQuery({
        queryKey: ['paperSummary', currentPaper?.id],
        queryFn: async () => {
            if (!authToken || !currentPaper) return null;

            // Check cache first
            if (summaryCache[currentPaper.id]) {
                return summaryCache[currentPaper.id];
            }

            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/summary`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch paper summary');
            }

            const data = await response.json();

            // Cache the summary
            setSummaryCache(prev => ({
                ...prev,
                [currentPaper.id]: data
            }));

            return data;
        },
        ...QUERY_CONFIG,
        enabled: !!authToken && !!currentPaper &&
            (paperStatus?.status === 'completed' || statusCache[currentPaper?.id]?.status === 'completed'),
    });

    // Create a mutation for saving notes
    const saveMutation = useMutation({
        mutationFn: async (newNoteContent) => {
            const response = await fetch(`${API_URL}/notes/save`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    paperId: currentPaper.id,
                    content: newNoteContent,
                })
            });

            const data = await response.json();
            console.log("saved note for paper", currentPaper.id, data);

            if (!response.ok) {
                throw new Error('Failed to save note');
            }

            return data;
        },
        onMutate: async (newNoteContent) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries(['notes', currentPaper?.id, authToken]);

            // Get the current notes
            const previousNotes = queryClient.getQueryData(['notes', currentPaper?.id, authToken]);

            // Optimistically update the notes
            queryClient.setQueryData(['notes', currentPaper?.id, authToken], old => [...(old || []), newNoteContent]);

            // Update the cache
            setNotesCache(prev => ({
                ...prev,
                [currentPaper.id]: [...(prev[currentPaper.id] || []), newNoteContent]
            }));

            return { previousNotes };
        },
        onError: (err, newNote, context) => {
            // Rollback on error
            queryClient.setQueryData(['notes', currentPaper?.id, authToken], context.previousNotes);
        },
        onSettled: () => {
            // Refetch after error or success
            queryClient.invalidateQueries(['notes', currentPaper?.id, authToken]);
        },
    });

    const handleNavigation = (path) => {
        navigate(path);
    };

    // Helper Components
    const NavItem = ({ icon, label, path, active }) => (
        <button
            onClick={() => {
                // Always navigate with the current paper if it exists
                navigate(path, currentPaper ? {
                    state: { paper: currentPaper }
                } : undefined);
            }}
            className={`p-3 rounded-xl transition-colors ${active ? 'bg-purple-600/20 text-purple-400' : 'text-gray-400 hover:bg-purple-600/10'
                }`}
            title={label}
        >
            {icon}
        </button>
    );

    const PaperListItem = ({ title, authors, active, onClick }) => (
        <div
            onClick={onClick}
            className={`p-3 rounded-lg cursor-pointer transition-colors ${active
                ? 'bg-purple-600/20 border border-purple-500/50'
                : 'hover:bg-purple-600/10'
                }`}
        >
            <h3 className={`text-sm font-medium mb-1 ${active ? 'text-purple-400' : 'text-white'}`}>
                {title}
            </h3>
            <p className="text-xs text-gray-400">{authors}</p>
        </div>
    );

    const fetchPaperHtml = useCallback(async (paperId) => {
        if (!authToken || !paperId) return;

        // Check if HTML is already in cache
        if (htmlCache[paperId]) {
            setPaperHtml(htmlCache[paperId]);
            return;
        }

        setIsLoadingHtml(true);
        try {
            const response = await fetch(`${API_URL}/papers/${paperId}/html`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch paper HTML');
            }

            const data = await response.json();
            // Store in cache
            setHtmlCache(prev => ({
                ...prev,
                [paperId]: data.html
            }));
            setPaperHtml(data.html);
        } catch (error) {
            console.error('Error fetching paper HTML:', error);
        } finally {
            setIsLoadingHtml(false);
        }
    }, [authToken, htmlCache]);

    const handleTabChange = (tabName) => {
        setActiveTab(tabName);
        if (tabName === 'read' && currentPaper) {
            fetchPaperHtml(currentPaper.id);
        }
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.2, 3)); // Changed from 2 to 3
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.2, 0.2)); // Changed from 0.5 to 0.2
    };

    const handleSaveNote = async () => {
        if (!newNote.trim() || !currentPaper || !authToken) return;

        setIsSavingNote(true);
        try {
            await saveMutation.mutateAsync(newNote);
            setNewNote(''); // Clear input on success
        } catch (error) {
            console.error('Error saving note:', error);
        } finally {
            setIsSavingNote(false);
        }
    };

    useEffect(() => {
        if (currentPaper) {
            // Set HTML from cache if available
            setPaperHtml(htmlCache[currentPaper.id] || null);

            // If "read" tab is active and HTML isn't cached, fetch it
            if (activeTab === 'read' && !htmlCache[currentPaper.id]) {
                fetchPaperHtml(currentPaper.id);
            }
        } else {
            setPaperHtml(null);
        }
    }, [currentPaper, htmlCache, activeTab, fetchPaperHtml]);

    const getPdfLink = useCallback((paper) => {
        if (!paper) return null;

        // Generate PDF link
        let pdfLink = paper.pdfLink;
        if (!pdfLink) {
            if (paper.id.includes('arxiv.org')) {
                pdfLink = paper.id.replace('/abs/', '/pdf/').replace('/html/', '/pdf/');
            } else {
                // If paper.id is just the arxiv ID (e.g. "2501.04519")
                pdfLink = `https://arxiv.org/pdf/${paper.id}`;
            }
        }

        return pdfLink;
    }, []);

    useEffect(() => {
        if (currentPaper) {
            setMountedPapers(prev => ({
                ...prev,
                [currentPaper.id]: true
            }));
        }
    }, [currentPaper]);

    useEffect(() => {
        if (Object.keys(mountedPapers).length > MAX_CACHED_PDFS) {
            const papers = Object.entries(mountedPapers);
            const oldestPapers = papers
                .slice(0, papers.length - MAX_CACHED_PDFS)
                .map(([id]) => id);

            setMountedPapers(prev => {
                const newCache = { ...prev };
                oldestPapers.forEach(id => delete newCache[id]);
                return newCache;
            });
        }
    }, [mountedPapers]);

    // Add this function to handle mindmap selection
    const handleMindmapSelect = (mindmap) => {
        setSelectedMindmap(mindmap);
    };

    // Reset initialLoadStartTime when paper changes
    useEffect(() => {
        setInitialLoadStartTime(null);
    }, [currentPaper]);

    // Update the mindmap status query
    const { data: mindmapStatus } = useQuery({
        queryKey: ['mindmapStatus', currentPaper?.id],
        queryFn: async () => {
            if (!authToken || !currentPaper) return null;

            // Check cache first
            if (statusCache[`mindmap-${currentPaper.id}`]) {
                return statusCache[`mindmap-${currentPaper.id}`];
            }

            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/mindmap/status`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch mindmap status');
            }

            const data = await response.json();

            // Cache completed or error statuses
            if (data.status === 'completed' || data.status === 'error') {
                setStatusCache(prev => ({
                    ...prev,
                    [`mindmap-${currentPaper.id}`]: data
                }));
            }

            if (data.status === 'processing' && mindmapStatusCheckAttempts < MAX_STATUS_CHECK_ATTEMPTS) {
                setMindmapStatusCheckAttempts(prev => prev + 1);
                throw new Error('Still processing');
            }

            return data;
        },
        ...STATUS_CHECK_CONFIG,
        enabled: !!authToken && !!currentPaper && !statusCache[`mindmap-${currentPaper?.id}`],
    });

    // Add this useEffect to reset attempts when paper changes
    useEffect(() => {
        setMindmapStatusCheckAttempts(0);
        setMindmapStatusCheckFailed(false);
    }, [currentPaper]);

    // Keep both queries
    const { data: mindmapTopics } = useQuery({
        queryKey: ['mindmapTopics', currentPaper?.id],
        queryFn: async () => {
            if (!authToken || !currentPaper) return null;

            // Check cache first
            if (mindmapCache[`topics-${currentPaper.id}`]) {
                return mindmapCache[`topics-${currentPaper.id}`];
            }

            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/mindmap-topics`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch mindmap topics');
            }

            const data = await response.json();

            // Cache the topics
            setMindmapCache(prev => ({
                ...prev,
                [`topics-${currentPaper.id}`]: data.topics
            }));

            return data.topics;
        },
        enabled: !!authToken && !!currentPaper &&
            (mindmapStatus?.status === 'completed' || statusCache[`mindmap-${currentPaper?.id}`]?.status === 'completed'),
        ...QUERY_CONFIG,
    });

    const { data: paperMindmaps } = useQuery({
        queryKey: ['paperMindmaps', currentPaper?.id],
        queryFn: async () => {
            if (!authToken || !currentPaper) return null;

            const response = await fetch(`${API_URL}/papers/${currentPaper.id}/mindmaps`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch mindmaps');
            }

            const data = await response.json();
            console.log("Raw mindmaps response:", data); // Log the raw response

            // Check if data is an array directly
            const mindmapsArray = Array.isArray(data) ? data : data.mindmaps;

            if (!mindmapsArray) {
                console.error("No mindmaps array found in response:", data);
                return {};
            }

            console.log("Processing mindmaps array:", mindmapsArray);

            // Store the entire mindmap object by topic name
            const mindmapsById = {};
            mindmapsArray.forEach(mindmap => {
                mindmapsById[mindmap.topicName] = mindmap;
            });

            return mindmapsById;
        },
        enabled: !!authToken && !!currentPaper && mindmapStatus?.status === 'completed',
        ...QUERY_CONFIG,
    });

    // Update the initial mindmap selection effect
    useEffect(() => {
        if (paperMindmaps && mindmapTopics && mindmapTopics.length > 0 && !selectedMindmap) {
            // Select the first mindmap by default
            const firstTopic = mindmapTopics[0];
            const firstMindmap = paperMindmaps[firstTopic.topicName];
            if (firstMindmap) {
                console.log("Setting initial mindmap:", firstMindmap.graph);
                setSelectedMindmap(firstMindmap.graph);
            }
        }
    }, [paperMindmaps, mindmapTopics]);

    // Add this centered loading component
    const CenteredLoading = ({ message, description }) => (
        <div className="h-full w-full flex items-center justify-center">
            <div className="flex flex-col items-center justify-center text-center">
                <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-lg text-purple-400 mt-4">{message}</p>
                {description && <p className="text-sm text-gray-400 mt-2">{description}</p>}
            </div>
        </div>
    );

    // Update the LoadingOrErrorState component
    const LoadingOrErrorState = () => {
        console.log("LoadingOrErrorState - status:", paperStatus?.status, "tab:", activeTab);

        const loadingMessages = {
            'keypoints': {
                title: 'Analyzing Key Points',
                description: 'Extracting the main points from the paper...'
            },
            'abbreviations': {
                title: 'Processing Abbreviations',
                description: 'Identifying technical terms and their meanings...'
            },
            'detailed-summary': {
                title: 'Generating Detailed Summary',
                description: 'Creating a comprehensive summary of the paper...'
            },
            'mcqs': {
                title: 'Creating Practice Questions',
                description: 'Generating multiple choice questions based on the paper...'
            },
            'summary': {
                title: 'Processing Abstract',
                description: 'Analyzing the paper abstract...'
            },
            'mindmaps': {
                title: 'Generating Mind Maps',
                description: 'Creating visual representations of key concepts...'
            },
            'qa': {
                title: 'Preparing Chat',
                description: 'Setting up the AI assistant for questions...'
            },
            'read': {
                title: 'Loading Paper',
                description: 'Preparing the paper for reading...'
            }
        };

        // Get the appropriate message for the current tab
        const currentMessage = loadingMessages[activeTab] || {
            title: 'Processing Content',
            description: 'Analyzing the paper...'
        };

        // Return loading state with the appropriate message
        return (
            <div className="h-full w-full flex items-center justify-center">
                <div className="flex flex-col items-center justify-center text-center">
                    <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-lg text-purple-400 mt-4">{currentMessage.title}</p>
                    <p className="text-sm text-gray-400 mt-2">{currentMessage.description}</p>
                    {paperStatus?.status === 'processing' && (
                        <p className="text-xs text-gray-500 mt-2">
                            Attempt {statusCheckAttempts + 1} of {MAX_STATUS_CHECK_ATTEMPTS}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    // Add an effect to handle paper changes from navigation
    useEffect(() => {
        if (location.state?.paper) {
            console.log("Updating paper from navigation:", location.state.paper);
            setCurrentPaper(location.state.paper);
        }
    }, [location.state]);

    // Add this effect to save answers when they change
    useEffect(() => {
        localStorage.setItem('mcqAnswers', JSON.stringify(mcqAnswers));
    }, [mcqAnswers]);

    return (
        <div className="flex h-screen bg-black text-white overflow-hidden">
            {/* Left Sidebar - Main Navigation */}
            <div className="w-16 border-r border-purple-900/30 flex flex-col items-center py-4">
                <div className="flex-1 flex flex-col items-center space-y-6">
                    <NavItem icon={<Search />} label="Search" path="/search" active={location.pathname === '/search'} />
                    <NavItem icon={<Layout />} label="Workspace" path="/workspace" active={location.pathname === '/workspace'} />
                    <NavItem icon={<Book />} label="Library" path="/library" active={location.pathname === '/library'} />
                    <NavItem icon={<CreditCard />} label="Subscription" path="/subscription" active={location.pathname === '/subscription'} />
                </div>
                <LogoutButton />
            </div>

            {/* Paper List Panel - Modified to be collapsible */}
            <div className={`transition-all duration-300 ease-in-out border-r border-purple-900/30 bg-black/50 overflow-hidden flex relative ${isCollapsed ? 'w-0' : 'w-80'}`}>
                <div className="min-w-[320px] overflow-y-auto">
                    <div className="p-4">
                        <h2 className="text-lg font-semibold text-purple-400 mb-4">My Papers</h2>
                        <div className="space-y-3">
                            {libraryPapers.map((paper) => (
                                <PaperListItem
                                    key={paper.id}
                                    title={paper.title}
                                    authors={paper.authors.join(', ')}
                                    active={currentPaper?.id === paper.id}
                                    onClick={() => {
                                        setCurrentPaper(paper);
                                    }}
                                />
                            ))}
                            {libraryPapers.length === 0 && (
                                <p className="text-gray-400 text-sm">No papers in your library yet</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Toggle Button - Fixed positioning */}
                <button
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className={`fixed top-1/2 -translate-y-1/2 bg-purple-900/30 hover:bg-purple-900/50 
                        transition-all duration-300 p-1.5 rounded-full z-50
                        border border-purple-900/50 group
                        ${isCollapsed ? 'left-16' : 'left-[336px]'}`}
                >
                    {isCollapsed ? (
                        <ChevronRight className="w-4 h-4 text-purple-400 group-hover:text-purple-300" />
                    ) : (
                        <ChevronLeft className="w-4 h-4 text-purple-400 group-hover:text-purple-300" />
                    )}
                </button>
            </div>

            <div className="flex-1 flex flex-col overflow-hidden bg-black">
                {/* Paper Title Bar */}
                <div className="h-16 border-b border-purple-900/30 flex items-center justify-between px-6">
                    <h1 className="text-xl font-semibold">
                        {currentPaper ? currentPaper.title : 'Select a Paper'}
                    </h1>
                </div>

                {/* Tabs */}
                {currentPaper && (
                    <div className="h-12 border-b border-purple-900/30 flex items-center px-6 gap-6">
                        <TabButton
                            label="Abstract"
                            active={activeTab === 'summary'}
                            onClick={() => handleTabChange('summary')}
                        />
                        <TabButton
                            label="Key Points"
                            active={activeTab === 'keypoints'}
                            onClick={() => handleTabChange('keypoints')}
                        />
                        <TabButton
                            label="Abbreviations"
                            active={activeTab === 'abbreviations'}
                            onClick={() => handleTabChange('abbreviations')}
                        />
                        <TabButton
                            label="Detailed Summary"
                            active={activeTab === 'detailed-summary'}
                            onClick={() => handleTabChange('detailed-summary')}
                        />
                        <TabButton
                            label="Paper"
                            active={activeTab === 'paper'}
                            onClick={() => handleTabChange('paper')}
                        />
                        <TabButton
                            label="Mindmaps"
                            active={activeTab === 'mindmaps'}
                            onClick={() => handleTabChange('mindmaps')}
                        />
                        <TabButton
                            label="Chat"
                            active={activeTab === 'qa'}
                            onClick={() => handleTabChange('qa')}
                        />
                        <TabButton
                            label="Notes"
                            active={activeTab === 'notes'}
                            onClick={() => handleTabChange('notes')}
                        />
                        <TabButton
                            label="Read Paper (beta)"
                            active={activeTab === 'read'}
                            onClick={() => handleTabChange('read')}
                        />
                        <TabButton
                            label="MCQs"
                            active={activeTab === 'mcqs'}
                            onClick={() => handleTabChange('mcqs')}
                        />
                    </div>
                )}

                {/* Content Area */}
                <div className="flex-1 relative overflow-hidden bg-black">
                    <div className="absolute inset-0">
                        {Object.entries(mountedPapers).map(([paperId]) => (
                            <div
                                key={paperId}
                                className="absolute inset-0"
                                style={{
                                    visibility: activeTab === 'paper' && currentPaper?.id === paperId ? 'visible' : 'hidden',
                                    zIndex: activeTab === 'paper' && currentPaper?.id === paperId ? 1 : 0
                                }}
                            >
                                <iframe
                                    src={getPdfLink({ id: paperId })}
                                    className="w-full h-full border-0"
                                    title={`pdf-${paperId}`}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="absolute inset-0" style={{
                        visibility: activeTab !== 'paper' ? 'visible' : 'hidden',
                        zIndex: activeTab !== 'paper' ? 1 : 0
                    }}>
                        {/* All other tab content goes here */}
                        {activeTab === 'summary' && currentPaper && (
                            <div className="h-full overflow-y-auto p-6">
                                <div className="max-w-3xl mx-auto space-y-6">
                                    {/* Authors */}
                                    <div className="flex flex-wrap gap-2 text-sm text-gray-400">
                                        {currentPaper.authors.map((author, index) => (
                                            <span key={index} className="px-2 py-1 bg-purple-900/20 rounded-full">
                                                {author}
                                            </span>
                                        ))}
                                    </div>

                                    {/* Publication Date */}
                                    <div className="text-sm text-gray-400">
                                        Published: {new Date(currentPaper.published).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </div>

                                    {/* Abstract */}
                                    <div className="prose prose-invert prose-purple max-w-none">
                                        <h2 className="text-xl font-semibold text-purple-400 mb-4">Abstract</h2>
                                        <p className="text-gray-300 leading-relaxed">{currentPaper.summary}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'keypoints' && currentPaper && (
                            <div className="h-full overflow-y-auto p-6 flex items-center justify-center">
                                <div className="max-w-3xl w-full">
                                    {!paperStatus || paperStatus.status === 'processing' ? (
                                        <LoadingOrErrorState />
                                    ) : paperStatus.status === 'error' ? (
                                        <LoadingOrErrorState />
                                    ) : paperSummary?.summary?.key_points ? (
                                        <section className="bg-purple-900/10 rounded-lg border border-purple-900/20 p-6">
                                            <h2 className="text-2xl font-semibold text-purple-400 mb-4">Key Points</h2>
                                            <ul className="list-disc list-inside space-y-2">
                                                {paperSummary.summary.key_points.map((point, index) => (
                                                    <li key={index} className="text-gray-300">{point}</li>
                                                ))}
                                            </ul>
                                        </section>
                                    ) : null}
                                </div>
                            </div>
                        )}

                        {activeTab === 'abbreviations' && currentPaper && (
                            <div className="h-full overflow-y-auto p-6 flex items-center justify-center">
                                <div className="max-w-3xl w-full">
                                    {!paperStatus || paperStatus.status === 'processing' ? (
                                        <LoadingOrErrorState />
                                    ) : paperStatus.status === 'error' ? (
                                        <LoadingOrErrorState />
                                    ) : paperSummary?.summary?.abbreviations ? (
                                        <section className="bg-purple-900/10 rounded-lg border border-purple-900/20 p-6">
                                            <h2 className="text-2xl font-semibold text-purple-400 mb-4">Abbreviations</h2>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {paperSummary.summary.abbreviations.map((abbr, index) => {
                                                    const [term, definition] = abbr.split(' - ');
                                                    return (
                                                        <div key={index} className="flex gap-2">
                                                            <span className="text-purple-400 font-medium">{term}:</span>
                                                            <span className="text-gray-300">{definition}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    ) : null}
                                </div>
                            </div>
                        )}

                        {activeTab === 'detailed-summary' && (
                            <div className="h-full overflow-y-auto">
                                {!paperStatus || paperStatus.status === 'processing' ? (
                                    <LoadingOrErrorState />
                                ) : paperStatus.status === 'error' ? (
                                    <LoadingOrErrorState />
                                ) : (
                                    <PaperSummaryView summaryData={paperSummary} />
                                )}
                            </div>
                        )}

                        {activeTab === 'mindmaps' && (
                            <div className="h-full flex">
                                {!mindmapStatus ? (
                                    // Initial loading state
                                    <div className="flex-1">
                                        <div className="h-full w-full flex items-center justify-center">
                                            <div className="flex flex-col items-center justify-center text-center">
                                                <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                                <p className="text-lg text-purple-400 mt-4">Loading mindmap status...</p>
                                                <p className="text-sm text-gray-400 mt-2">This may take a few minutes...</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : mindmapStatus.status === 'error' ? (
                                    // Error state
                                    <div className="flex-1">
                                        <div className="h-full w-full flex items-center justify-center">
                                            <div className="flex flex-col items-center justify-center text-center">
                                                <p className="text-lg text-red-400">Failed to generate mindmaps</p>
                                                <p className="text-sm text-gray-400 mt-2">Please try again later</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : mindmapStatus.status === 'processing' ? (
                                    // Processing state with attempt counter
                                    <div className="flex-1">
                                        <div className="h-full w-full flex items-center justify-center">
                                            <div className="flex flex-col items-center justify-center text-center">
                                                <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                                <p className="text-lg text-purple-400 mt-4">Generating Mind Maps</p>
                                                <p className="text-sm text-gray-400 mt-2">
                                                    Our AI is analyzing the paper to create interactive mind maps.
                                                    This may take a few minutes.
                                                </p>
                                                <p className="text-xs text-gray-500 mt-2">
                                                    Attempt {mindmapStatusCheckAttempts + 1} of {MAX_STATUS_CHECK_ATTEMPTS}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ) : mindmapStatus.status === 'completed' ? (
                                    // Completed state with mindmap viewer and topics
                                    <>
                                        <div className="flex-1">
                                            {selectedMindmap ? (
                                                <CustomMindmapViewer mindmapData={selectedMindmap} />
                                            ) : (
                                                <div className="h-full flex items-center justify-center text-gray-400">
                                                    <p>Select a topic to view its mindmap</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Topics Panel */}
                                        <div className="w-80 border-l border-purple-900/30 bg-black/50 overflow-y-auto">
                                            <div className="p-4">
                                                <h3 className="text-lg font-semibold text-purple-400 mb-4">Topics</h3>
                                                <div className="space-y-2">
                                                    {!mindmapTopics ? (
                                                        <div className="flex items-center justify-center py-8">
                                                            <div className="flex flex-col items-center gap-3">
                                                                <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
                                                                <p className="text-sm text-gray-400">Loading topics...</p>
                                                            </div>
                                                        </div>
                                                    ) : mindmapTopics.length === 0 ? (
                                                        <div className="text-center text-gray-400 py-4">
                                                            <p>No topics available</p>
                                                        </div>
                                                    ) : (
                                                        mindmapTopics.map((topic, index) => (
                                                            <div
                                                                key={index}
                                                                className="p-3 bg-purple-900/10 rounded-lg border border-purple-900/20"
                                                            >
                                                                <div className="flex justify-between items-start gap-2 mb-2">
                                                                    <h3 className="text-sm font-medium text-purple-400">
                                                                        {topic.topicName}
                                                                    </h3>
                                                                    <Button
                                                                        onClick={() => {
                                                                            const mindmap = paperMindmaps?.[topic.topicName];
                                                                            console.log("Selected mindmap:", mindmap);
                                                                            if (mindmap?.graph) {
                                                                                console.log("Setting mindmap graph:", mindmap.graph);
                                                                                setSelectedMindmap(mindmap.graph);
                                                                            } else {
                                                                                console.warn("No mindmap graph found for topic:", topic.topicName);
                                                                            }
                                                                        }}
                                                                        className={`px-2 py-1 rounded text-xs ${selectedMindmap === paperMindmaps?.[topic.topicName]?.graph
                                                                            ? 'bg-purple-600 text-white'
                                                                            : 'bg-purple-600/20 text-purple-400 hover:bg-purple-600/30'
                                                                            }`}
                                                                    >
                                                                        View Mindmap
                                                                    </Button>
                                                                </div>
                                                                <p className="text-xs text-gray-400">{topic.description}</p>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                        )}

                        {activeTab === 'qa' && (
                            <ChatView
                                currentPaper={currentPaper}
                                authToken={authToken}
                            />
                        )}

                        {activeTab === 'notes' && (
                            <div className="h-full overflow-y-auto p-6">
                                <div className="max-w-3xl mx-auto space-y-6">
                                    {/* Note Input Section */}
                                    <div className="bg-purple-900/10 rounded-lg border border-purple-900/20 p-4">
                                        <div className="text-sm text-gray-400 mb-2">
                                            Supports Markdown formatting: **bold**, *italic*, # headers, - lists, quotes, `code`
                                        </div>
                                        <textarea
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            placeholder="Add your notes about this paper... Use Markdown for formatting"
                                            className="w-full min-h-[150px] px-4 py-3 bg-black/50 border border-purple-900/30 rounded-lg 
                                            text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 resize-none"
                                        />
                                        <div className="mt-3 flex justify-end">
                                            <Button
                                                onClick={handleSaveNote}
                                                disabled={isSavingNote || !newNote.trim()}
                                                className="bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 disabled:opacity-40 
                                                disabled:cursor-not-allowed transition-all duration-200 rounded-xl px-6 py-2.5 
                                                flex items-center gap-2 disabled:hover:bg-purple-600/20"
                                            >
                                                {isSavingNote ? (
                                                    <>
                                                        <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                                                        Saving...
                                                    </>
                                                ) : (
                                                    <>
                                                        <FileText className="w-4 h-4" />
                                                        Save Note
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    {savedNotes.length > 0 && (
                                        <div className="flex items-center gap-4">
                                            <div className="h-px flex-1 bg-purple-900/30"></div>
                                            <span className="text-sm text-gray-400">Previous Notes</span>
                                            <div className="h-px flex-1 bg-purple-900/30"></div>
                                        </div>
                                    )}

                                    {/* Notes List */}
                                    <div className="space-y-4">
                                        {savedNotes.map((note, index) => (
                                            <div
                                                key={index}
                                                className="bg-purple-900/10 rounded-lg border border-purple-900/20 p-4"
                                            >
                                                <ReactMarkdown
                                                    className="prose prose-invert prose-purple max-w-none"
                                                    components={{
                                                        // Style markdown elements
                                                        p: ({ node, ...props }) => <p className="text-gray-300 mb-4" {...props} />,
                                                        h1: ({ node, ...props }) => <h1 className="text-purple-400 text-2xl font-bold mb-4" {...props} />,
                                                        h2: ({ node, ...props }) => <h2 className="text-purple-300 text-xl font-bold mb-3" {...props} />,
                                                        h3: ({ node, ...props }) => <h3 className="text-purple-200 text-lg font-bold mb-2" {...props} />,
                                                        ul: ({ node, ...props }) => <ul className="list-disc list-inside text-gray-300 mb-4" {...props} />,
                                                        ol: ({ node, ...props }) => <ol className="list-decimal list-inside text-gray-300 mb-4" {...props} />,
                                                        li: ({ node, ...props }) => <li className="text-gray-300 mb-1" {...props} />,
                                                        code: ({ node, ...props }) => (
                                                            <code className="bg-purple-900/20 text-purple-300 px-1 py-0.5 rounded" {...props} />
                                                        ),
                                                        blockquote: ({ node, ...props }) => (
                                                            <blockquote className="border-l-4 border-purple-500 pl-4 italic text-gray-400" {...props} />
                                                        ),
                                                    }}
                                                >
                                                    {note}
                                                </ReactMarkdown>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Empty State */}
                                    {savedNotes.length === 0 && (
                                        <div className="text-center text-gray-400 mt-8">
                                            <p>No notes yet. Start adding your thoughts about this paper!</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {activeTab === 'read' && (
                            <ReadPaperView
                                isLoadingHtml={isLoadingHtml}
                                paperHtml={paperHtml}
                                currentPaper={currentPaper}
                            />
                        )}

                        {activeTab === 'mcqs' && (
                            <div className="h-full overflow-y-auto">
                                {!paperStatus || paperStatus.status === 'processing' ? (
                                    <LoadingOrErrorState />
                                ) : paperStatus.status === 'error' ? (
                                    <LoadingOrErrorState />
                                ) : (
                                    <McqView
                                        mcqs={paperSummary?.summary?.mcqs}
                                        answers={mcqAnswers}
                                        setAnswers={setMcqAnswers}
                                        paperId={currentPaper?.id}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WorkspacePage;